import {
    getSocket, setSocket,
    getIsGenerating, setIsGenerating,
    getReconnectInterval, setReconnectInterval,
    getPageContext,
    getCredential,
} from './state';
import {
    getMessageBubble,
    showTypingIndicator, hideTypingIndicator,
    enableInput, disableInput,
    chatInput,
    releaseMessageBubble,
    getSenderTypeByAIResponseType,
} from './ui';
import { AIRequestType, ResponseMessage } from '../types/Message';

// ─── Connection ───────────────────────────────────────────────────────────────

export function connectWebSocket(): void {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wspath = document.querySelector<HTMLMetaElement>('meta[name="wspath"]')!.content;
    const socketUrl = `${protocol}//${window.location.host}${wspath}`;

    console.log(`Connecting to WebSocket: ${socketUrl}`);
    setConnectionState('connecting');

    const ws = new WebSocket(socketUrl);
    setSocket(ws);

    ws.onopen = () => {
        console.log('WebSocket connection established.');
        setConnectionState('online');
        setReconnectInterval(1000); // Reset exponential backoff
    };

    ws.onmessage = (event) => {
        try {
            const message = JSON.parse(event.data);
            handleIncomingMessage(message);
        } catch (err) {
            console.error('Error parsing WebSocket message: ', err);
        }
    };

    ws.onclose = () => {
        console.warn('WebSocket connection closed.');
        setConnectionState('offline');
        scheduleReconnect();
    };

    ws.onerror = (error) => {
        console.error('WebSocket error: ', error);
        setConnectionState('offline');
    };
}

function scheduleReconnect(): void {
    const interval = getReconnectInterval();
    console.log(`Attempting to reconnect in ${interval}ms...`);
    setTimeout(() => {
        connectWebSocket();
    }, interval);
}

// ─── Connection State UI ──────────────────────────────────────────────────────

import { statusDot, statusText, connectionBanner } from './ui.js';

export function setConnectionState(state: 'online' | 'connecting' | 'offline'): void {
    statusDot.className = 'status-dot';

    if (state === 'online') {
        statusDot.classList.add('online');
        statusText.innerText = 'Connected';
        connectionBanner.classList.add('hidden');
        if (!getIsGenerating()) enableInput();
    } else if (state === 'connecting') {
        statusDot.classList.add('connecting');
        statusText.innerText = 'Connecting...';
        disableInput();
    } else {
        statusDot.classList.add('offline');
        statusText.innerText = 'Disconnected';
        connectionBanner.classList.remove('hidden');
        disableInput();
    }
}

// ─── Incoming Message Handler ─────────────────────────────────────────────────

function handleIncomingMessage(msg: ResponseMessage): void {
    console.log('Incoming message: ', msg);
    advancePulseTime();

    if (msg.text) {
        const bubble = getMessageBubble(msg.id, getSenderTypeByAIResponseType(msg.type));
        msg.reset && bubble.clear();
        bubble.addContent(msg.text, msg.type);
        bubble.render();
    }
    if (msg.type == 'reasoning' || msg.type == 'pulse') {
        showTypingIndicator('Thinking', msg.id);
    } else if (msg.type == 'response') {
        showTypingIndicator('responding', msg.id);
    } else {
        hideTypingIndicator();
    }
    if (msg.type == 'finish') {
        setIsGenerating(false);
        releaseMessageBubble('ai', msg.id);
        releaseMessageBubble('error', msg.id);
        enableInput();
        chatInput.focus();
    }
}

// ─── Send Message ─────────────────────────────────────────────────────────────
export function sendPulse(): void {
    sendMessage('pulse', '');
}
export function sendMessage(type: AIRequestType, text: string): void {
    const socket = getSocket();
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    if (type == 'requestAIResponse') {
        if (!text || getIsGenerating()) return;

        setIsGenerating(true);
        disableInput();
        const bubble = getMessageBubble('' + Date.now(), 'user');
        bubble.addContent(text);
        bubble.render();
        socket.send(JSON.stringify({
            type: type,
            text: text,
            pageContext: JSON.stringify(getPageContext().content),
            token: getCredential().token,
            sessionId: getCredential().sessionId,
        }));
        showTypingIndicator('Thinking');
    } else {
        socket.send(JSON.stringify({
            type: type,
            text: text,
            token: getCredential().token,
            sessionId: getCredential().sessionId,
        }));
    }
    advancePulseTime();
}

// ─── Keep alive ────
const pulseInterval = 20000;
let nextPulseTime = 0;
function advancePulseTime() {
     nextPulseTime = Date.now() + pulseInterval;
}
const keepAlive = () => {
    if(Date.now() > nextPulseTime) {
        sendPulse();
    }
    setTimeout(keepAlive, 1000);
}
advancePulseTime();
setTimeout(keepAlive, 1000);
